import React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.jsx";
import { ErrorBoundary, registerServiceWorker } from "@thekrystalship/krystal";
import { noteSignInRefusal, writeStoredUser } from "./lib/authStorage.js";
import { sessionStore } from "./lib/sessionStore.js";

// Global styles. Order matters: the component class library (which opens with the
// design tokens — variables + @font-face), then consumer overrides.
import "./styles/kit.css";
import "./styles/consumer.css";

// Theme preference store (client-only). Importing it applies the saved theme to
// <html data-theme>, wires the meta tag, and live-tracks the OS scheme for "auto".
// The index.html boot script already set the attribute pre-paint; this keeps the
// store + browser-chrome color in sync. The package declares that module side-effectful,
// so a bare import of it keeps the theme applied.
import "@thekrystalship/krystal";

// The session is settled BEFORE anything mounts, so the gate knows on its first render whether this
// browser holds one. The provider sending a browser back lands on its own path with a code in the
// query: the package exchanges it for a session and answers the route the browser left from, which is
// put back before the hash router reads it. Any other load restores what is stored, which asks nobody
// anything.
async function boot() {
  const landing = sessionStore.isLanding();
  const refusal = landing ? new URLSearchParams(location.search).get("error") : null;
  const returnTo = await sessionStore.restore();
  if (landing) {
    if (!sessionStore.isLive()) noteSignInRefusal(refusal || (sessionStore.provider() ? "sign_in_failed" : "no_provider"));
    const back = returnTo && returnTo.startsWith("/#") ? returnTo : "/";
    try { history.replaceState(null, "", back); } catch { /* the router reads what is there */ }
  }
  // Dev convenience: when `npm run dev` seeds an auth-DISABLED local kgsm-api
  // (.env.development → VITE_API_BASE), sign in automatically so dev boots straight
  // into the app instead of stalling in front of it. Gated to dev builds → DCE'd in production; a
  // no-op against an auth-ENABLED seed. See connect.js devSeedAutoConnect. An auth-disabled host has no
  // session to restore — its session is opened against it — so it is opened here, on every dev load,
  // before the identity check below reads one.
  let openedSeed = false;
  if (import.meta.env.DEV && !sessionStore.isLive() && import.meta.env.VITE_API_BASE) {
    try {
      const { devSeedAutoConnect } = await import("./lib/connect.js");
      await devSeedAutoConnect(import.meta.env.VITE_API_BASE);
      openedSeed = true;
      await sessionStore.authorize();
    } catch {}
  }
  // The stored identity mounts the shell, and the session record is what the shell settles on. An
  // identity with no record behind it — the stored session removed by a refusal, or no
  // provider recorded at all — is a shell nothing will ever authorize, so it is dropped here and the
  // gate, which can sign somebody in, takes the load instead. So is one whose dev seed did not open.
  if (!sessionStore.getState().session || (openedSeed && !sessionStore.isLive())) writeStoredUser(null);
  createRoot(document.getElementById("root")).render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>
  );
  // Install the PWA shell SW (the build declares it; the dev server has none). Done after mount so it
  // never contends with first paint.
  registerServiceWorker();
  sessionStore.confirmProvider().catch(() => {});
}
boot();
